import styled from "@emotion/styled";
import ReservationItem, { getBackgroundColor, getBorderColor, TColor } from "./component/reservation-item";
import { TReservation } from "apis/meetings/meeting-reservation";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { endTimeToIndex, getSelectedTimeBlockIndex, startTimeToIndex } from "./utils";
import UI from "@mile-meeting/mile-ui";
interface ExtendedReservation extends TReservation {
  startIndex: number;
  endIndex: number;
  overlaps: number;
  left?: number;
}
const calculateOverlap = (reservations: TReservation[], selectedDate: Date): ExtendedReservation[] => {
  const extendedReservations: ExtendedReservation[] = reservations.map((reservation) => {
    let startIndex = startTimeToIndex(new Date(reservation.startTime));
    let endIndex = endTimeToIndex(new Date(reservation.endTime));

    //* 회의가 선택된 일자의 오후 11시 이후에 종료하는 경우
    if (new Date(reservation.endTime).getTime() > new Date(selectedDate).setHours(23, 0, 0, 0)) {
      endIndex = 32;
    }

    //* 회의가 선택된 일자의 오전 7시 이전에 시작하는 경우
    if (new Date(reservation.startTime).getTime() < new Date(selectedDate).setHours(7, 0, 0, 0)) {
      startIndex = 0;
    }

    //* 회의가 선택된 일자의 오후 11시 이후에 시작하는 경우
    if (new Date(reservation.startTime).getTime() > new Date(selectedDate).setHours(23, 0, 0, 0)) {
      startIndex = -1;
      endIndex = -1;
    }

    //* 회의가 선택된 일자의 오전 7시 이전에 종료하는 경우
    if (new Date(reservation.endTime).getTime() < new Date(selectedDate).setHours(7, 0, 0, 0)) {
      startIndex = -1;
      endIndex = -1;
    }

    return {
      ...reservation,
      startIndex,
      endIndex, // 7시부터 23시까지의 인덱스
      overlaps: 1, // 초기값은 자기 자신 포함
    };
  });

  // 회의가 겹치는 그룹을 추적하기 위한 배열
  const overlapGroups: ExtendedReservation[][] = [];

  extendedReservations.forEach((reservation) => {
    let addedToGroup = false;

    // 이미 존재하는 겹치는 그룹에 추가 가능한지 체크
    for (let group of overlapGroups) {
      const hasOverlap = group.some(
        (other) => other.startIndex < reservation.endIndex && other.endIndex > reservation.startIndex
      );

      if (hasOverlap) {
        group.push(reservation);
        addedToGroup = true;
        break;
      }
    }

    // 겹치는 그룹이 없으면 새 그룹 생성
    if (!addedToGroup) {
      overlapGroups.push([reservation]);
    }
  });

  // 각 그룹 내에서 회의들의 overlaps 값과 위치를 계산
  overlapGroups.forEach((group) => {
    const groupLength = group.length;
    group.forEach((reservation, idx) => {
      reservation.overlaps = groupLength;
      reservation.left = (idx * 100) / groupLength; // 겹치는 회의들의 left 값을 배치
    });
  });

  return extendedReservations;
};

const MeetingRoomReservationList = ({
  reservation,
  color,
  meetingRoom,
  selectedTime,
  isSelectedMeetingRoom,
  isSelectedTimeBlock,
  selectedMeetingRoom,
  onUpdateTimeTable,
  onHandleEdit,
  onClickEmptyBlock,
  selectedDate,
  isEditing = false,
  selectedRsv,
  editingRsvTitle,
  editRsvColor,
}: {
  reservation?: TReservation[];
  color: TColor;
  meetingRoom?: TMeetingRoom;
  selectedMeetingRoom?: TMeetingRoom; //* 선택된 회의실입니다. 해당 회의실에 대한 예약 현황을 보여줍니다.
  selectedTime?: { startDate?: Date; endDate?: Date }; //* 예약하기 모달에서 선택된 시간입니다. 해당 시간을 가지고, 타임테이블에 타임 블록을 보여줍니다.
  isSelectedMeetingRoom?: boolean; //* 선택된 회의실인지 여부, 선택된 회의실일 경우, 타임블록을 보여줍니다.
  isSelectedTimeBlock?: boolean; //* 타임블록을 선택하고 수정하는 것이 가능한지 여부
  onUpdateTimeTable?: () => void;
  onHandleEdit?: (reservation?: TReservation, meetingRoom?: TMeetingRoom, isMyReservation?: boolean) => void;
  onClickEmptyBlock?: (startHour: number, startMinutes: number, endHour: number, endMinutes: number) => void;
  selectedDate?: Date;
  isEditing?: boolean;
  selectedRsv?: TReservation;
  editingRsvTitle?: string;
  editRsvColor?: TColor; //* 수정할 예약의 색상입니다. 해당 색상으로 타임블록을 표시합니다.
}) => {
  const reservationsWithOverlap = calculateOverlap(reservation ?? [], selectedDate ?? new Date());

  //* 빈 시간 블록을 클릭했을 때 해당 블록이 어떤 시간대인지 계산
  const getEmptyBlockTime = (startIndex: number, endIndex: number) => {
    const startHour = Math.floor(startIndex / 2) + 7;
    const startMinutes = startIndex % 2 === 0 ? 0 : 30;
    const endHour = Math.floor(endIndex / 2) + 7;
    const endMinutes = endIndex % 2 === 0 ? 0 : 30;

    return { startIndex: { startHour, startMinutes }, endIndex: { endHour, endMinutes } };
  };

  //* hover 하면 표시될 타임테이블의 높이를 계산
  const hoverTimeBlockHeight = (index?: number) => {
    if (selectedTime?.startDate && selectedTime?.endDate) {
      const { startDate, endDate } = selectedTime;
      const height = getSelectedTimeBlockIndex(startDate, endDate).height;

      // 선택된 위젯의 종료시간이 23시를 넘어가면, 23시까지만 표시
      if (index && index + height / 28 > 32) {
        return 28 * (32 - index);
      }
      return height;
    } else return 28;
  };
  const isToday = selectedDate ? new Date(selectedDate).toDateString() === new Date().toDateString() : false;

  //* (수정시) 선택된 시간 & 회의실이 기존 선택된 예약과 일치하는지 여부
  const isEditedTimeBlock = () => {
    if (selectedRsv?.startTime && selectedRsv?.endTime && selectedTime?.startDate && selectedTime?.endDate) {
      const meetingStart = new Date(selectedRsv.startTime);
      const meetingEnd = new Date(selectedRsv.endTime);

      //* 같은 시간인지 여부를 확인합니다.
      const isSameTime =
        meetingStart.getHours() === selectedTime.startDate.getHours() &&
        meetingStart.getMinutes() === selectedTime.startDate.getMinutes();

      //* 같은 회의실인지 여부를 확인합니다.
      const isSameMeetingRoom = selectedMeetingRoom?.id === selectedRsv.meetingRoom?.id;

      return isSameTime && isSameMeetingRoom;
    }
  };

  //* SelectedTimeBlock 컴포넌트 : 예약현황에서 "수정시" 선택된 시간 블록을 표시합니다.
  //* SelectedNewTimeBlock 컴포넌트 : 예약하기 모달에서 "예약시" 선택된 시간 블록을 표시합니다.
  return (
    <Container>
      {isSelectedTimeBlock &&
        selectedTime?.startDate &&
        selectedTime.endDate &&
        isSelectedMeetingRoom &&
        !isEditing && (
          <SelectedNewTimeBlock
            style={{
              height: getSelectedTimeBlockIndex(selectedTime.startDate, selectedTime.endDate).height,
              top: getSelectedTimeBlockIndex(selectedTime.startDate, selectedTime.endDate).top,
              position: "absolute",
            }}
          />
        )}
      {isSelectedTimeBlock &&
        selectedTime?.startDate &&
        selectedTime.endDate &&
        isSelectedMeetingRoom &&
        isEditing &&
        editRsvColor && (
          <SelectedTimeBlock
            style={{
              height: getSelectedTimeBlockIndex(
                selectedTime?.startDate ?? new Date(),
                selectedTime?.endDate ?? new Date()
              ).height,
              top: getSelectedTimeBlockIndex(selectedTime?.startDate ?? new Date(), selectedTime?.endDate ?? new Date())
                .top,
              position: "absolute",
              backgroundColor: getBackgroundColor(editRsvColor),
              border: `2px dashed ${getBorderColor(editRsvColor)}`,
              padding:
                getSelectedTimeBlockIndex(selectedTime?.startDate ?? new Date(), selectedTime?.endDate ?? new Date())
                  .height > 28
                  ? "12px"
                  : "4px 12px",
              gap:
                getSelectedTimeBlockIndex(selectedTime?.startDate ?? new Date(), selectedTime?.endDate ?? new Date())
                  .height <= 56
                  ? "0px"
                  : "4px",
            }}
          >
            <SelectedTimeBlockTitle>
              {selectedRsv?.isMyMeeting && (
                <SelectedTimeBlockCircle style={{ backgroundColor: getBorderColor(editRsvColor) }} />
              )}
              <UI.Typography
                type="caption01"
                fontStyle="bold"
                color="var(--alias-color-label-normal)"
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                }}
              >
                {editingRsvTitle ?? ""}
              </UI.Typography>
            </SelectedTimeBlockTitle>
            {getSelectedTimeBlockIndex(selectedTime?.startDate ?? new Date(), selectedTime?.endDate ?? new Date())
              .height > 28 && (
              <UI.Typography
                type="caption02"
                fontStyle="bold"
                color={getBorderColor(editRsvColor)}
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                }}
              >
                {(selectedTime?.startDate ?? new Date()).getHours().toString().padStart(2, "0")}:
                {(selectedTime?.startDate ?? new Date()).getMinutes().toString().padStart(2, "0")} ~
                {(selectedTime?.endDate ?? new Date()).getHours().toString().padStart(2, "0")}:
                {(selectedTime?.endDate ?? new Date()).getMinutes().toString().padStart(2, "0")}
              </UI.Typography>
            )}
          </SelectedTimeBlock>
        )}
      {isSelectedTimeBlock &&
        Array.from({ length: 32 }, (_, index) => (
          <TimeBlock
            key={index}
            onClick={() => {
              //* 오늘 날짜이고, 현재 시간보다 이전 시간을 클릭했을 때는 클릭이 되지 않습니다.
              if (isToday && index < startTimeToIndex(new Date())) return;

              //* 선택된 시간이 없을 때는, 디폴트 30분간격의 타임테이블로 예약하기 모달을 띄웁니다.
              if (!selectedTime) {
                const { startIndex, endIndex } = getEmptyBlockTime(index, index + 1);
                onClickEmptyBlock?.(
                  startIndex.startHour,
                  startIndex.startMinutes,
                  endIndex.endHour,
                  endIndex.endMinutes
                );
                return;
              }
              //* 선택된 시간이 있을 때는, 선택된 시간을 기준으로 타임테이블을 띄웁니다.
              const { startIndex, endIndex } = getEmptyBlockTime(index, index + hoverTimeBlockHeight() / 28);
              onClickEmptyBlock?.(startIndex.startHour, startIndex.startMinutes, endIndex.endHour, endIndex.endMinutes);
            }}
          >
            {isToday === true && index >= startTimeToIndex(new Date()) && (
              <div className="interaction" style={{ height: hoverTimeBlockHeight(index) }}>
                <div className="interaction-child"></div>
              </div>
            )}
            {isToday === false && (
              <div className="interaction" style={{ height: hoverTimeBlockHeight(index) }}>
                <div className="interaction-child"></div>
              </div>
            )}
          </TimeBlock>
        ))}
      {reservationsWithOverlap.map((meeting, index) => {
        const width = 100 / meeting.overlaps;
        return (
          <ReservationItem
            key={`${meeting.id}-${index}`}
            onUpdate={onUpdateTimeTable}
            onHandleEdit={onHandleEdit}
            meetingRoom={meetingRoom}
            color={color}
            styles={{
              width: `${width}%`,
              left: `${meeting.left}%`,
              opacity:
                isEditing &&
                (meeting.id ? meeting.id === selectedRsv?.id : meeting.googleEventId === selectedRsv?.googleEventId)
                  ? isEditedTimeBlock()
                    ? 0
                    : 0.4
                  : 1,
            }}
            top={meeting.startIndex * 28}
            height={(meeting.endIndex - meeting.startIndex) * 28}
            reservation={meeting}
          />
        );
      })}
    </Container>
  );
};
export default MeetingRoomReservationList;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;

  min-width: 100px;
  max-width: 280px;
`;

//* 빈 시간 블록을 나타내는 컴포넌트
const TimeBlock = styled.div`
  height: 28px;
  width: 100%;
  cursor: pointer;
  position: relative;

  .interaction {
    &:active {
      .interaction-child {
        border-radius: 8px;
        opacity: 0.09;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--alias-color-label-normal);
      }
    }
  }

  &:hover {
    .interaction {
      position: relative;
      max-width: 280px;
      border-radius: 8px;
      border: 2px dashed rgba(173, 178, 184, 1);
      box-sizing: border-box;
      background: var(--component-color-fill-normal);
    }
  }
`;

const SelectedTimeBlock = styled.div`
  height: 28px;
  width: 100%;
  cursor: pointer;
  position: relative;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  justify-content: space-between;
  max-width: 280px;
  border-radius: 8px;
  border: 2px dashed rgba(173, 178, 184, 1);
  box-sizing: border-box;
  background: var(--component-color-fill-normal);
`;

const SelectedTimeBlockTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const SelectedTimeBlockCircle = styled.div`
  width: 10px;
  height: 10px;

  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
`;

const SelectedNewTimeBlock = styled.div`
  height: 28px;
  width: 100%;
  cursor: pointer;
  position: relative;

  max-width: 280px;
  border-radius: 8px;
  border: 2px dashed rgba(173, 178, 184, 1);
  box-sizing: border-box;
  background: var(--component-color-fill-normal);
`;
